import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useController, useFormContext } from 'react-hook-form';
import { FormfieldWrapper } from '@bevy/bevy-design-system';

import { RichTextEditor, BaseToolbar } from '@/shared/components/ui';

import styles from './styles.module.less';

/**
 * React Hook From Controlled Rich Text Input
 * @param {string} name
 * @param {string} label
 * @param {number} maxLength
 * @param {string} maxLengthMessage
 * @param {function} onHandleDrop
 * @param {function} imageLoader
 * @param {boolean} isDroppingImage
 * @see {Documentation} https://react-hook-form.com/docs/usecontroller
 * @see {Documentation} https://tiptap.dev/
 * @returns {React.Component}
 * @example
 * export default function EmailDescription() {
  const [isDroppingImage, setIsDroppingImage] = useState(false);

  const onHandleDrop = (view, event, slice, moved) => {
    // the dropped file
    const file = event?.dataTransfer?.files[0];
    // Return default behavior if moving a file,
    // or we didn't receive a file
    if (moved || !file) return false;
    // Handle drop if the file wasn't moved (ie dropped) and file exists
    // First upload image file to server
    uploadImageBeforeDrop({
      file,
      // Container to store on server
      imageType: 'newsletters',
      // Callback to drop successfull image into editor
      editorCallback: (url) => dropImageIntoProseEditor(url, view, event),
      onLoading: setIsDroppingImage,
    });
    // handled drop
    return true;
  };

  return (
    <ControlledRichTextInput
      name='content'
      label={translate('dashboard.newsletterForm.messageInput.title', 'Message')}
      showCharacterCount
      onHandleDrop={onHandleDrop}
      isLoading={isDroppingImage}
      maxLength={12}
      maxLengthMessage={translate(
        'dashboard.maxChars',
        { single: 'Maximum {{count}} character', plural: 'Maximum {{count}} characters' },
        { count: 12 },
      )}
    />
  );
}
 */
export default function ControlledRichTextInput({
  name,
  label,
  size = 'small',
  showCharacterCount = false,
  maxLength = null,
  maxLengthMessage = '',
  onHandleDrop = () => {},
  imageLoader = null,
  isLoading = false,
  AiControls,
  relatedFields,
  rules = { required: false },
  editorMenus,
} = {}) {
  // Store the character count of the rich text editor
  const [valueCount, setValueCount] = useState(0);

  const { control } = useFormContext();

  // Formfieldwrapper expects a string value to display character count
  // Here we simply turn the count into a string of 'x' repeated count times
  const fieldStringValue = useMemo(() => 'x'.repeat(valueCount > 0 ? valueCount : 0), [valueCount]);

  const {
    field: richTextField,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      ...rules,
      validate: {
        maxLength: () => !maxLength || valueCount <= maxLength || maxLengthMessage,
        ...rules?.validate,
      },
    },
  });

  return (
    <div data-help-id={name}>
      <FormfieldWrapper
        size={size}
        inputID={name}
        labelText={label}
        value={fieldStringValue}
        maxLength={maxLength}
        showCharacterCount={showCharacterCount && maxLength >= 0}
        errors={error ? [error?.message] : null}
      >
        <div
          role='textbox'
          className={classNames(styles.richTextFieldWrapper, {
            [styles.error]: error,
            [styles.loading]: isLoading,
          })}
        >
          <div className={styles.padding}>
            <RichTextEditor
              characterLimit={maxLength || null}
              onCharacterCount={(count) => {
                setValueCount(count);
              }}
              onChange={richTextField.onChange}
              onBlur={richTextField.onBlur}
              content={richTextField?.value}
              FixedToolbar={BaseToolbar}
              onHandleDrop={onHandleDrop}
              imageLoader={imageLoader}
              editorMenus={editorMenus}
            />
          </div>
          {AiControls && (
            <AiControls
              value={richTextField.value || ''}
              name={name}
              length={fieldStringValue.length}
              relatedFields={relatedFields}
              maxLength={maxLength}
            />
          )}
        </div>
      </FormfieldWrapper>
    </div>
  );
}

ControlledRichTextInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']),
  showCharacterCount: PropTypes.bool,
  maxLength: PropTypes.number,
  maxLengthMessage: PropTypes.string,
  onHandleDrop: PropTypes.func,
  imageLoader: PropTypes.func,
  editorMenus: PropTypes.node,
  isLoading: PropTypes.bool,
  rules: PropTypes.object,
  relatedFields: PropTypes.arrayOf(PropTypes.string),
  AiControls: PropTypes.elementType,
};
